<template>
    <div class="categories-selector">
        <CategoryPill
            category="all"
            :selected="!route.query.category"
            :link="`${pagePathWithoutPagination}${locationQueryToQueryString({...query, category: [], ...(currentTab && {type: currentTab})})}`"
        />
        <CategoryPill
            v-for="category in sortedCategories"
            :key="category.uid"
            :category="category.uid"
            :label="category.label"
            :selected="route.query.category === category.uid"
            :link="`${pagePathWithoutPagination}${locationQueryToQueryString({
                ...query,
                category: category.uid,
                ...(currentTab && {type: currentTab}),
            })}`"
        />
    </div>
</template>
<script setup lang="ts">
    import type {ResourceCategoryDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {computed, createContentHelpers, useAsyncData, useTranslation} from '#imports';
    import CategoryPill from '~/components/CategoryPill.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';
    import {CATEGORIES} from '../app/keys';

    defineProps<{currentTab?: string}>();

    const {t} = useTranslation();

    const route = useRoute('resources-articles-page');
    const {
        path,
        params: {page},
        query,
    } = route;
    const pagePathWithoutPagination = page ? path.split('/').slice(0, -1).join('/') : path;
    const {locationQueryToQueryString} = createContentHelpers();

    const {data} = await useAsyncData(
        'resourcesCategories',
        () => usePrismicSearch<ResourceCategoryDocument>({type: 'resourceCategory', pageSize: 100}),
        {deep: true},
    );

    const sortedCategories = computed(() => {
        const resourceCategories = data.value?.results || [];
        const resourceCategoryUids = new Set(resourceCategories.map((category) => category.uid));
        const staticCategories = CATEGORIES.filter((category) => !resourceCategoryUids.has(category.toLowerCase()));

        return [
            ...resourceCategories.map((category) => ({
                uid: category.uid ?? '',
                label: category.data.name || '',
            })),
            ...staticCategories.map((category) => ({
                uid: category,
                label: t(`resources.categories.${category}`),
            })),
        ].sort((a, b) => a.label.localeCompare(b.label));
    });
</script>

<style lang="scss">
    .categories-selector {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }
</style>
